import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminThemeDetail.styled";
import { facilityService, BuildingFacility } from "services/facilityService";
import { adminThemeService } from "services/adminThemeService";
import { Theme } from "types";
import AdminThemeFacilityModal from "./AdminThemeFacilityModal";

interface Props {
  theme: Theme;
  onClose: () => void;
  onUpdate?: () => void;
}

const AdminThemeDetail: React.FC<Props> = ({ theme, onClose, onUpdate }) => {
  const [facilities, setFacilities] = useState<BuildingFacility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(theme.name);
  const [isSaving, setIsSaving] = useState(false);

  const fetchFacilities = useCallback(() => {
    facilityService
      .getThemeFacilities(theme.id)
      .then(setFacilities)
      .catch(() => {});
  }, [theme.id]);

  useEffect(() => {
    setIsLoading(true);
    facilityService
      .getThemeFacilities(theme.id)
      .then(setFacilities)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [theme.id]);

  const handleEditStart = () => {
    setEditName(theme.name);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setIsSaving(true);
    try {
      await adminThemeService.updateTheme(theme.id, { name: trimmed });
      setIsEditing(false);
      onUpdate?.();
    } catch {
      // 실패 시 유지
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (facilityId: number) => {
    setRemovingId(facilityId);
    try {
      await adminThemeService.removeFacility(theme.id, facilityId);
      setFacilities((prev) => prev.filter((f) => f.id !== facilityId));
    } catch {
      // 실패 시 목록 유지
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Panel onClick={(e) => e.stopPropagation()}>

        {/* 헤더 */}
        <S.PanelHeader>
          <S.PanelTitle>{isEditing ? "테마 수정" : "테마 상세정보"}</S.PanelTitle>
          <S.HeaderActions>
            {!isEditing && (
              <S.EditButton onClick={handleEditStart}>수정</S.EditButton>
            )}
            <S.CloseButton onClick={onClose}>✕</S.CloseButton>
          </S.HeaderActions>
        </S.PanelHeader>

        {/* 바디 */}
        <S.PanelBody>
          {isEditing ? (
            /* ── 수정 폼 ── */
            <>
              <S.FormGroup>
                <S.FormLabel>테마명</S.FormLabel>
                <S.FormInput
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") handleEditCancel();
                  }}
                  placeholder="테마명을 입력하세요"
                  autoFocus
                  disabled={isSaving}
                />
              </S.FormGroup>

              <S.FormActions>
                <S.CancelButton onClick={handleEditCancel} disabled={isSaving}>
                  취소
                </S.CancelButton>
                <S.SaveButton onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "저장 중..." : "저장"}
                </S.SaveButton>
              </S.FormActions>
            </>
          ) : (
            /* ── 조회 뷰 ── */
            <>
              <S.SectionHeader>
                <S.SectionLeft>
                  <S.SectionTitle>시설 목록</S.SectionTitle>
                  {!isLoading && <S.CountBadge>{facilities.length}개</S.CountBadge>}
                </S.SectionLeft>
                <S.AddButton onClick={() => setIsAddModalOpen(true)}>
                  + 시설 추가
                </S.AddButton>
              </S.SectionHeader>

              {isLoading ? (
                <S.StatusText>불러오는 중...</S.StatusText>
              ) : facilities.length === 0 ? (
                <S.EmptyWrap>
                  <S.EmptyText>이 테마에 배정된 시설이 없습니다.</S.EmptyText>
                </S.EmptyWrap>
              ) : (
                <S.FacilityList>
                  {facilities.map((f) => (
                    <S.FacilityCard key={f.id}>
                      {f.thumbnailImage ? (
                        <S.FacilityThumb src={f.thumbnailImage} alt={f.name} />
                      ) : (
                        <S.FacilityThumbPlaceholder>🏛️</S.FacilityThumbPlaceholder>
                      )}
                      <S.FacilityInfo>
                        <S.FacilityName>{f.name}</S.FacilityName>
                        <S.FacilityMeta>
                          {f.building?.name ?? "건물 미배정"}
                          {f.location ? ` · ${f.location}` : ""}
                        </S.FacilityMeta>
                      </S.FacilityInfo>
                      <S.RemoveButton
                        onClick={() => handleRemove(f.id)}
                        disabled={removingId === f.id}
                        title="테마에서 제거"
                      >
                        {removingId === f.id ? "…" : "✕"}
                      </S.RemoveButton>
                    </S.FacilityCard>
                  ))}
                </S.FacilityList>
              )}
            </>
          )}
        </S.PanelBody>

      </S.Panel>

      {isAddModalOpen && (
        <AdminThemeFacilityModal
          themeId={theme.id}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            fetchFacilities();
          }}
        />
      )}
    </S.Overlay>
  );
};

export default AdminThemeDetail;
